import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import SimpleMD from "@/components/app/SimpleMD";
import AISummaryDialog from "@/components/app/AISummaryDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, BookOpen, Plus, ChevronRight, ChevronDown, FileText, Trash2, Edit3, Save, X } from "lucide-react";
import { toast } from "sonner";

// Walk the flat list, depth-first, producing [{node, depth, hasChildren}, ...]
// so the JSX is a single flat map() with no recursive components — avoids babel parse explosions.
const flattenTree = (pages, expanded) => {
  const byParent = new Map();
  pages.forEach((p) => {
    const key = p.parent_page_id || "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(p);
  });
  byParent.forEach((arr) => arr.sort((a, b) => (a.title || "").localeCompare(b.title || "")));

  const out = [];
  const walk = (parentKey, depth) => {
    const kids = byParent.get(parentKey) || [];
    for (const node of kids) {
      const hasChildren = byParent.has(node.page_id);
      out.push({ node, depth, hasChildren });
      const isOpen = depth === 0 || expanded.has(node.page_id);
      if (hasChildren && isOpen) walk(node.page_id, depth + 1);
    }
  };
  walk("__root__", 0);
  return out;
};

const Wiki = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", markdown_content: "" });
  const [expanded, setExpanded] = useState(new Set());
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", parent_page_id: null });

  const flatTree = useMemo(() => flattenTree(pages, expanded), [pages, expanded]);

  const loadTree = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/wiki/tree`),
      ]);
      setProject(p.data);
      setPages(t.data);
      // Auto-select first root page if nothing selected
      if (!selectedId && t.data.length > 0) {
        const firstRoot = t.data.find((x) => !x.parent_page_id) || t.data[0];
        setSelectedId(firstRoot.page_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wiki");
    }
  }, [projectId, selectedId]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // Load page content when selectedId changes
  useEffect(() => {
    if (!selectedId) { setCurrentPage(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/wiki/pages/${selectedId}`);
        if (!cancelled) {
          setCurrentPage(data);
          setEditing(false);
        }
      } catch {
        if (!cancelled) setCurrentPage(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, projectId]);

  const toggleExpanded = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const startCreate = (parentId) => {
    setCreateForm({ title: "", parent_page_id: parentId || null });
    setOpenCreate(true);
  };

  const create = async () => {
    if (!createForm.title.trim()) return toast.error("Title required");
    try {
      const { data } = await api.post(`/projects/${projectId}/wiki/pages`, {
        title: createForm.title.trim(),
        markdown_content: "",
        parent_page_id: createForm.parent_page_id,
      });
      toast.success("Page created");
      if (createForm.parent_page_id) {
        const next = new Set(expanded);
        next.add(createForm.parent_page_id);
        setExpanded(next);
      }
      setOpenCreate(false);
      setSelectedId(data.page_id);
      setEditing(true);
      setEditForm({ title: data.title, markdown_content: data.markdown_content || "" });
      loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    }
  };

  const startEdit = () => {
    if (!currentPage) return;
    setEditForm({ title: currentPage.title, markdown_content: currentPage.markdown_content || "" });
    setEditing(true);
  };

  const save = async () => {
    if (!editForm.title.trim()) return toast.error("Title required");
    try {
      const { data } = await api.patch(`/projects/${projectId}/wiki/pages/${currentPage.page_id}`, {
        title: editForm.title.trim(),
        markdown_content: editForm.markdown_content,
      });
      setCurrentPage(data);
      setEditing(false);
      toast.success("Saved");
      loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async () => {
    if (!currentPage) return;
    if (!window.confirm(`Delete "${currentPage.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/projects/${projectId}/wiki/pages/${currentPage.page_id}`);
      toast.success("Deleted");
      setSelectedId(null);
      setCurrentPage(null);
      loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `Wiki · ${project.name}` : "Wiki"}
        title="Project Wiki"
        description="Confluence-style knowledge base for this project. Nest pages freely; write in Markdown."
        actions={
          <div className="flex gap-2">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="back-to-project-from-wiki">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
            <Button onClick={() => startCreate(null)} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-wiki-page-btn">
              <Plus className="w-4 h-4 mr-1" /> New page
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-12 min-h-[70vh]">
        {/* Tree sidebar */}
        <aside className="col-span-12 lg:col-span-3 border-r border-gray-200 bg-gray-50">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-violet-600" strokeWidth={1.5} />
            <span className="font-display font-bold text-sm tracking-tight text-gray-900">Pages</span>
            <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-gray-400 font-mono">{pages.length}</span>
          </div>
          {pages.length === 0 ? (
            <div className="px-4 py-8 text-sm text-gray-500" data-testid="wiki-empty">
              No pages yet. Create your first one →
            </div>
          ) : (
            <div className="py-2" data-testid="wiki-tree">
              {flatTree.map(({ node, depth, hasChildren }) => {
                const isOpen = depth === 0 || expanded.has(node.page_id);
                const isSelected = selectedId === node.page_id;
                return (
                  <div
                    key={node.page_id}
                    className={`flex items-center gap-1 px-2 py-1.5 text-sm cursor-pointer group ${
                      isSelected ? "bg-violet-600 text-white" : "hover:bg-gray-100 text-gray-700"
                    }`}
                    style={{ paddingLeft: 8 + depth * 14 }}
                    onClick={() => setSelectedId(node.page_id)}
                    data-testid={`wiki-tree-node-${node.page_id}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExpanded(node.page_id); }}
                      className={`flex-shrink-0 ${hasChildren ? "" : "invisible"}`}
                    >
                      {hasChildren && (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                    </button>
                    <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? "" : "text-gray-400"}`} strokeWidth={1.6} />
                    <span className="truncate flex-1">{node.title}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); startCreate(node.page_id); }}
                      className={`opacity-0 group-hover:opacity-100 ${isSelected ? "hover:text-white" : "hover:text-[#002FA7]"}`}
                      title="Add sub-page"
                      data-testid={`wiki-add-child-${node.page_id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* Page viewer / editor */}
        <main className="col-span-12 lg:col-span-9 px-5 sm:px-8 lg:px-12 py-8">
          {!currentPage ? (
            <div className="text-sm text-gray-500" data-testid="wiki-no-selection">
              {pages.length === 0 ? "Click 'New page' to start your wiki." : "Pick a page from the sidebar."}
            </div>
          ) : editing ? (
            <div className="space-y-4" data-testid="wiki-editor">
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="rounded-none text-3xl font-display font-black tracking-tighter border-0 border-b border-[#1F2937] px-0 focus-visible:ring-0 focus-visible:border-[#002FA7] h-auto py-2"
                data-testid="wiki-title-input"
              />
              <Textarea
                value={editForm.markdown_content}
                onChange={(e) => setEditForm({ ...editForm, markdown_content: e.target.value })}
                placeholder="Write in Markdown. **bold**, lists with - and headings with #."
                rows={22}
                className="rounded-none font-mono text-sm leading-relaxed"
                data-testid="wiki-body-input"
              />
              <div className="flex gap-2">
                <Button onClick={save} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="wiki-save-btn">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
                <Button onClick={() => setEditing(false)} variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="wiki-cancel-edit-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <article data-testid="wiki-viewer">
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter" data-testid="wiki-page-title">
                  {currentPage.title}
                </h1>
                <div className="flex gap-2">
                  <AISummaryDialog
                    targetType="wiki_page"
                    projectId={projectId}
                    targetId={currentPage.page_id}
                    label="AI summary"
                    testId="wiki-ai-summary-btn"
                  />
                  <Button onClick={startEdit} variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="wiki-edit-btn">
                    <Edit3 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button onClick={remove} variant="ghost" className="rounded-none text-rose-700" data-testid="wiki-delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-400 mb-6">
                Last edited by {currentPage.last_modified_by_name || "—"} · {new Date(currentPage.updated_at).toLocaleString()}
              </div>
              {currentPage.markdown_content ? (
                <SimpleMD text={currentPage.markdown_content} />
              ) : (
                <p className="text-sm text-gray-500 italic">Empty page. Click <strong>Edit</strong> to start writing.</p>
              )}
            </article>
          )}
        </main>
      </div>

      {/* Create page dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md" data-testid="wiki-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {createForm.parent_page_id ? "New sub-page" : "New top-level page"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              autoFocus
              placeholder="Page title"
              className="rounded-none"
              value={createForm.title}
              onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              data-testid="wiki-create-title-input"
            />
            {createForm.parent_page_id && (
              <div className="text-[11px] text-gray-500 bg-[#030712] border border-[#1F2937] px-3 py-2">
                Under: <span className="font-mono">{pages.find((p) => p.page_id === createForm.parent_page_id)?.title}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={create} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="wiki-confirm-create-btn">
              Create page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wiki;
